/**
 * A React functional component that renders a profile menu and a confirm modal.
 * It also uses the react-i18next library for translation and the react-redux library for dispatching actions.
 * @returns The rendered React component.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import ProfileMenu from "../topbarDropdown/profileMenu";
import ConfirmModal from "../confirmModal/confirmModel";
import { useNavigate } from "react-router-dom";
/**
 * Represents the header component of the application.
 * @param {object} props - The props passed to the component.
 * @returns The rendered header component.
 */
function Header(props) {
  /**
   * Manages the state of the logout modal and provides translation functionality.
   * @param {boolean} logoutModal - The state of the logout modal.
   * @param {function} setLogoutModal - A function to update the state of the logout modal.
   * @returns None
   */
  const [logoutModal, setLogoutModal] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Toggles the menu by calling the toggleMenuCallback function provided in the props.
   * @returns None
   */
  const toggleMenu = () => {
    props.toggleMenuCallback();
  };

  /**
   * Handles the logout functionality by setting the logout modal state to true.
   * @returns None
   */
  const handleLogout = () => {
    setLogoutModal(true);
  };

  /**
   * Function to handle the logout action.
   * It sets the logout modal state to false and dispatches a toast message to indicate successful logout.
   * @returns None
   */
  const logoutFunction = () => {
    setLogoutModal(false);
    localStorage.clear();
    navigate("/");
  };

  const closeLogoutModel = () => {
    setLogoutModal(false);
  };

  /**
   * Renders a header component with a toggle menu button and a profile menu button.
   * Also renders a confirmation modal for logging out.
   * @param {function} toggleMenu - Function to toggle the menu.
   * @param {function} handleLogout - Function to handle the logout action.
   * @param {boolean} logoutModal - Boolean value indicating whether the logout modal should be shown or not.
   * @param {function} logoutFunction - Function to execute the logout action.
   * @param {function} closeLogoutModel - Function to close the logout modal.
   * @returns JSX elements representing the header component and the logout confirmation modal.
   */
  return (
    <>
      {/**
       * Renders the header component for the top bar of the page.
       * @returns {JSX.Element} - The rendered header component.
       */}
      <header id="page-topbar">
        <div className="navbar-header">
          <div className="d-flex">
            <button
              type="button"
              onClick={toggleMenu}
              className="btn btn-sm font-size-16 header-item BurgerMenu"
              id="vertical-menu-btn"
            >
              <i className="fa fa-fw fa-bars"></i>
            </button>
          </div>
          <div className="d-flex ">
            <ProfileMenu handleLogout={handleLogout} />
          </div>
        </div>
      </header>

      {/**
       * Renders a confirmation modal for logging out.
       * @param {string} modalTitle - The title of the modal.
       * @param {string} modalDescription - The description of the modal.
       * @param {boolean} isUnderline - Determines if the modal title should be underlined.
       * @param {boolean} showModal - Determines if the modal should be shown.
       * @param {string} leftBtnTitle - The title of the left button.
       * @param {boolean} isModelIcon - Determines if an icon should be displayed in the modal.
       * @param {string} iconColor - The color of the icon.
       * @param {string} iconCricle - The class name for the circle around the icon.
       *
       */}
      <ConfirmModal
        modalTitle={t("LOGOUT")}
        modalDescription={t("ARE_YOU_SURE_YOU_WANT_TO_LOGOUT")}
        isUnderline={false}
        showModal={logoutModal}
        leftBtnTitle={t("YES_LOGOUT")}
        isModelIcon={true}
        iconColor={"mdi mdi-exit-to-app "}
        iconCricle={"iconCricleDelete"}
        leftBtnClass={"ok_Btn"}
        isFirstButton={true}
        isSecondButton={true}
        rightBtnClass="Border_Button"
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={logoutFunction}
        rightButtonClick={closeLogoutModel}
      />
    </>
  );
}

/**
 * PropTypes for the Header component.
 * @prop {any} t - The translation function.
 * @prop {any} toggleMenuCallback - The callback function to toggle the menu.
 * @prop {any} showRightSidebar - The flag to show the right sidebar.
 * @prop {function} toggleRightSidebar - The function to toggle the right sidebar.
 */
Header.propTypes = {
  t: PropTypes.any,
  toggleMenuCallback: PropTypes.any,
  showRightSidebar: PropTypes.any,
  toggleRightSidebar: PropTypes.func,
};

export default Header;
